import { Button } from "@portal/BaseComponents/Button/AntButton";
import { ScrollDrawer } from "@portal/BaseComponents/Drawer/ScrollDrawer";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { PopConfirm } from "@portal/BaseComponents/PopConfirm/AntPopConfirm";
import { PopConfirmContent } from "@portal/BaseComponents/PopConfirm/PopConfirmContent";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { ToolTip } from "@portal/BaseComponents/ToolTip/AntTooltip";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { getDisputeStatusDetails } from "@portal/Components/Portal/PotalTabs/Disputes/predicate";
import { usePrivileges } from "@portal/hooks/use-privilleges";
import useOpen from "@portal/hooks/useOpen";
import { Icon } from "@portal/lib/icons/icons";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { usePageNavigate } from "@portal/lib/router/hooks/usePageNavigae";
import {
  useDeleteDisputeQuery,
  useDisputeDetailsQuery,
} from "@portal/queries/dispute-query";
import { DetailsLayout, StatusTag } from "@sinecycle/growcomponents";
import { Fragment } from "react";
import { useParams } from "react-router-dom";
import { Comment } from "../../Comment/Comment";
import { DisputeEdit } from "../DisputeForm/Edit/DisputeEdit";
import { DisputeStatusSelect } from "../DisputeStatusSelect/DisputeStatusSelect";
import { DisputeDetailsComp } from "./Details/DisputeDetailsDetails";
import { DisputeDetailsViewProps } from "./type";
import CustomfieldDisplay from "../CustomfieldDisplay";
import { DRAWER_ZINDEX } from "@portal/BaseComponents/Drawer/predicate";

const DisputeIcon = (
  <Icon
    icon={["far", "comment-times"]}
    color="var(--orange-6)"
    style={{ paddingBlockStart: "2px" }}
  />
);

const EditIcon = <Icon icon={["far", "pen"]} />;
const DeleteIcon = <Icon icon={["far", "trash-alt"]} />;
const t = {
  title: "Dispute",
};
function DisputeDetailsView(props: DisputeDetailsViewProps) {
  const {
    id,
    open,
    header,
    headerTitle = t.title,
    actionButton,
    footer,
    onClose,
  } = props;

  const {
    open: commentOpen,
    open: editOpen,
    toggleOpen,
    onClose: handleEditClose,
    handleOpenChange: handleEditOpenChange,
  } = useOpen({ open: false });
  const navigate = usePageNavigate();
  const { getDisputePrivilege, current_user } = usePrivileges();
  const { customer_id } = useParams<AppPageParams>();
  const { data, isLoading } = useDisputeDetailsQuery(id);
  const handleDelete = useDeleteDisputeQuery({
    successCallBack() {
      handleClose();
    },
  });

  function onDelete() {
    handleDelete.mutateAsync();
  }

  const Content = (
    <PopConfirmContent
      title="Confirm Delete"
      desc="Do you want to delete this dispute?"
      descStyles={{ maxWidth: "160px" }}
    />
  );

  function getIsDisputeDelete() {
    return (
      getDisputePrivilege("DISPUTE_DELETE") &&
      current_user === data?.created_by?.id &&
      data?.source_app === "CUSTOMER_PORTAL"
    );
  }
  // function getIsDisputeEdit() {
  //   return (
  //     getDisputePrivilege("DISPUTE_UPDATE") &&
  //     current_user === data?.created_by.id &&
  //     data?.source_app === "CUSTOMER_PORTAL"
  //   );
  // }

  const isDisputeDelete = getIsDisputeDelete();
  // const isDisputeEdit = getIsDisputeEdit();
  const EditButton = (
    <ToolTip title="Edit Dispute">
      <Button icon={EditIcon} onClick={toggleOpen} />
    </ToolTip>
  );

  const DeleteButton = (
    <HideWrapper hide={!isDisputeDelete}>
      <PopConfirm
        onConfirm={onDelete}
        title={Content}
        cancelButtonProps={{ type: "text" }}
        placement="bottomLeft"
        icon={false}
        okText="Delete"
        okButtonProps={{ loading: handleDelete.isLoading }}
      >
        <ToolTip title="Delete Dispute">
          <Button icon={DeleteIcon} loading={handleDelete.isLoading} />
        </ToolTip>
      </PopConfirm>
    </HideWrapper>
  );

  const StatusSelect = (
    <HideWrapper hide={!getDisputePrivilege("DISPUTE_UPDATE")}>
      <DisputeStatusSelect value={data?.status} />
    </HideWrapper>
  );

  function handleClose() {
    if (!customer_id) return;
    onClose?.();
    navigate({
      appPage: "DISPUTES",
      pathParams: {
        customer_id,
      },
    });
  }

  const HeaderTitle = (
    <>
      {isLoading ? (
        <SkeletonInput />
      ) : (
        <Flex
          align="start"
          justify="center"
          space="2"
          vertical
          style={{ width: "100%" }}
        >
          <Flex space="8" align="start" justify="start">
            {DisputeIcon}
            <Flex vertical space={"0"}>
              <TypographyText strong fs="16">
                {`${headerTitle} `}
              </TypographyText>
              <TypographyText
                style={{
                  fontWeight: "var(--fs-regular)",
                  maxWidth: "120px",
                  color: "var(--orange-6)",
                }}
                fs="14"
                ellipsis={{
                  tooltip: data?.dispute_types.map((f) => f.name).join(", "),
                }}
              >
                {`${
                  data?.dispute_types
                    ? data.dispute_types.map((f) => f.name).join(", ")
                    : "-"
                } `}
              </TypographyText>
            </Flex>
          </Flex>
        </Flex>
      )}
    </>
  );

  const Status = data ? (
    <HideWrapper hide={getDisputePrivilege("DISPUTE_UPDATE")}>
      <StatusTag
        style={getDisputeStatusDetails(data.status).style}
        text={getDisputeStatusDetails(data.status).label}
      />
    </HideWrapper>
  ) : null;
  const ActionArea = (
    <>
      {isLoading ? (
        <SkeletonInput />
      ) : (
        <Flex align="center">
          {StatusSelect}
          {Status}
          {EditButton}
          {DeleteButton}
          {[...(actionButton ?? [])]?.map((action) => (
            <Fragment key={action.id}>{action.component}</Fragment>
          ))}
        </Flex>
      )}
    </>
  );
  const Header = header ? (
    header
  ) : (
    <FormHeader
      mode="drawer"
      onClose={handleClose}
      title={HeaderTitle}
      extra={ActionArea}
    />
  );

  const Footer = footer ? footer : null;
  const Comments = <Comment type="DRAWER" id={id} activity="disputes" />;

  function getFooterStyle() {
    return {
      padding: commentOpen ? 0 : "var(--space-12) var(--space-24)",
      backgroundColor: "var(--gray-2)",
    };
  }

  const footerStyle = getFooterStyle();
  const CustomFieldComponent = (
    <CustomfieldDisplay
      type={'DISPUTE'}
      customFieldData={data?.custom_field_data}
      isLoading={isLoading}
    />
  );
  return (<>
    <ScrollDrawer
      title={Header}
      open={open}
      footer={Footer}
      mask={true}
      maskClosable
      onClose={handleClose}
      styles={{
        wrapper: {
          background: "var(--gray-1)",
          boxShadow: "var(--shadow-4)",
          height: "var(--drawer-height)",
        },
        body: {
          padding: 0,
        },
        header: {
          padding: 0,
        },
        footer: footerStyle,
        mask: {
          position: "fixed",
          inset: 0,
        },
      }}
      width={840}
      push={false}
      destroyOnClose
      getContainer={false}
      zIndex={DRAWER_ZINDEX}
    >
      
        <DetailsLayout
          content={
            <DisputeDetailsComp
              data={data}
              loading={isLoading}
              footer={Comments}
            />
          }
          info={CustomFieldComponent}
          mode='detail-view'
        />
      </ScrollDrawer>
      <DisputeEdit
        value={data}
        open={editOpen}
        onClose={handleEditClose}
        key={String(editOpen)}
        onOpenChange={handleEditOpenChange}
      />
</>
  );
}

export { DisputeDetailsView };
